import React, { useContext, useEffect, useState } from 'react'
import { Navigate, useParams } from 'react-router-dom'

import HeaderBanner from '../component/topBar/HeaderBanner'
import BasicTabs from '../component/commonUnit/TabPanels'
import StudentPanel from '../component/student-components/StudentPanel'
import AudioStu from '../component/audioSTU/AudioStu'
import AudioSessionPanel from '../component/audioSTU/AudioSessionPanel'
import GuiExerciseEditor from '../component/topBar/GuiExerciseEditor'
import PopQuizEditor from '../component/session/PopQuizEditor'
import { isGuiSession, getGuiSessionConfig } from '../component/workspace/config/guiSessionConfig'
import { opensInWorkspace, usesVizmentalIDE } from '../component/session/studentView/studentSurface'

import { AuthContext } from '../context/AuthContext'
import { SelectedTestProvider } from '../context/SelectedTest'
import { GroupsProvider } from '../context/GroupsContext'
import { GroupsFilterProvider } from '../context/GroupsFilterContext'
import { ErrorContextProvider } from '../context/ErrorContext'
import { TestResultContextProvider } from '../context/TestResultContext'
import { AutoGroupingContextProvider } from '../context/AutoGroupingContext'
import { PresenterListProvider } from '../context/PresenterListContext'
import { CurrentStepContextProvider } from '../context/CurrentStepContext'
import { MessageProvider } from '../context/MessageContext'
import { SubmissionsProvider } from '../context/SubmissionsContext'
import { SessionContext } from '../context/SessionContext'

import { getSessionById, getSessionsByUserId, joinSession } from '../service/sessionService'
import { setSessionInUser, updateUser } from '../service/userService';
import { IncrementStuNumInSession } from '../service/sessionService';
import VizmentalIDE from '../component/student-components/VizmentalIDE'
import WaitingRoom from '../component/workspace/components/WaitingRoom'
import ReasoningGapStudent from '../component/reasoningGap/ReasoningGapStudent'
import ReasoningGapInstructor from '../component/reasoningGap/ReasoningGapInstructor'
import { isReasoningGapSession } from '../component/reasoningGap/reasoningGapConfig'
import ExperimentPage from '../component/session/experiment/ExperimentPage'
import ConditionBanner from '../component/session/experiment/ConditionBanner'
import ExperimentEntryScreen from '../component/session/experiment/ExperimentEntryScreen'
import {
  RETRY_DELAYS_MS,
  SLOW_RETRY_MS,
  delayFor,
  missingSession,
  needsExperimentEntry,
  useExperimentEntry,
  wait,
} from '../component/session/experiment/useExperimentEntry'
import { conditionTagOf, isExperimentSession } from '../component/session/experiment/experimentConfig'

import '../css/codeIssuePanel.scss'

const Home = React.memo(() => {
  const { currentUser, setCurrentUser } = useContext(AuthContext)
  const { session, setSession } = useContext(SessionContext)
  const { id: routeSessionId } = useParams()
  // Whether a student has a pre-existing user_session row for this GUI session, i.e. they
  // were actually invited via a /workspace/:id link before - null while unresolved (GUI
  // student case still loading, or n/a for every other case), true/false once known. See
  // the redirect check below for why this matters.
  const [hasGuiAccess, setHasGuiAccess] = useState(null)
  // The row fetched for this route. SessionContext's `session` can be replaced by its 10s
  // poll of currentUser.session_id (which a student on an experiment's URL never joins), so
  // decisions about the route itself read this instead.
  const [routeSession, setRouteSession] = useState(null)
  // Fetching that row: 'loading', 'slow' (still retrying after the quick retries), 'done', or
  // 'missing' (a student's link to no session: told so, and checked again now and then).
  const [routeLoad, setRouteLoad] = useState('loading')
  const [routeRetry, setRouteRetry] = useState(0)
  // A student on an experiment's link, or on a condition's link that may not be theirs: where
  // they belong (useExperimentEntry joins them there).
  const entry = useExperimentEntry(routeSessionId, routeSession)

  useEffect(() => {
    const sessionId = routeSessionId;
    let cancelled = false;
    setHasGuiAccess(null)
    setRouteSession(null)
    setRouteLoad('loading')

    // A failed or empty first fetch used to leave students on a blank page until they
    // refreshed (the effect died; an empty answer is also what the server sends when its
    // database is too busy). Retry, jittered so a class does not retry in lockstep: errors
    // until it answers, an empty answer a few times before taking it as no such session.
    const fetchRouteSession = async () => {
      for (let attempt = 0; ; attempt++) {
        if (attempt > 0) {
          if (attempt > RETRY_DELAYS_MS.length) setRouteLoad('slow')
          await wait(delayFor(attempt))
          if (cancelled) return undefined
        }
        try {
          const fetched = await getSessionById(sessionId)
          if (fetched || attempt >= RETRY_DELAYS_MS.length) return fetched
        } catch (error) {
          if (error?.response?.status === 404) return undefined
        }
        if (cancelled) return undefined
      }
    }

    const run = async () => {
      const fetchedSession = await fetchRouteSession();
      if (cancelled) return;
      // No session at this address (a mistyped number; E1-S3: 995 for 955): a student is told
      // so (below) instead of a spinner that never ends, and is not joined to it - the join
      // below wrote the missing id into their session_id and session_list. SessionContext
      // keeps their own session.
      if (!fetchedSession && (currentUser?.role === 3 || currentUser?.role === 4)) {
        setRouteSession(null);
        setRouteLoad('missing');
        return;
      }
      // A student who still has to be placed (useExperimentEntry): keep SessionContext on
      // their own session. Setting the experiment's row here could also land after
      // SessionContext's fetch of the condition they are sent to, and replace it.
      if (!needsExperimentEntry(fetchedSession, currentUser)) setSession(fetchedSession);
      setRouteSession(fetchedSession);
      setRouteLoad('done');

      if (!currentUser || !sessionId) return;
      // Role 1 is the instructor. Do not run the student join below: that pushes
      // session_list, increments stu_num, and writes a user_session row, which is how
      // opening a session was counting the instructor as a student. SessionContext still
      // loads and polls whatever is stored on currentUser.session_id, so leaving that
      // pointer stale makes every other session render as the old one (the URL changes,
      // the content snaps back). Remember the session they actually opened, and nothing else.
      if (currentUser.role === 1) {
        if (String(currentUser.session_id) !== String(sessionId)) {
          const nextSessionId = Number(sessionId)
          // An instructor-table sign-in (kind, from /validate) keeps it as its current_session:
          // its id is not a user id, and updateUser wrote the student who shares the number.
          const saved = currentUser.kind === 'instructor'
            ? await setSessionInUser(currentUser.id, nextSessionId)
            : await updateUser({ id: currentUser.id, session_id: nextSessionId })
          if (cancelled) return
          if (saved) setCurrentUser({ ...currentUser, session_id: nextSessionId })
        }
        return
      }

      const isStudent = currentUser.role === 3 || currentUser.role === 4;
      const isGui = isGuiSession(fetchedSession);

      // An experiment's own row is not an exercise: students are never joined to it (they
      // work in one of its condition sessions). A condition's link may belong to another
      // student's condition. In both cases useExperimentEntry joins them where they belong;
      // when that is here, it sets session_id to this route and this effect runs again.
      if (needsExperimentEntry(fetchedSession, currentUser)) return;

      if (isStudent && !isGui && opensInWorkspace(fetchedSession)) {
        // Python-family students are redirected to /workspace/:id below, and its
        // useSessionJoin records the join - joining here too would write it twice.
        return;
      }

      let priorAccess = true;
      if (isGui && isStudent) {
        // GUI sessions are meant to be reached via a /workspace/:id link the instructor
        // shared (see Distribute button). A student who lands on /session/:id itself -
        // the instructor's authoring URL - without ever having visited /workspace/:id
        // before has not actually been invited, so don't treat landing here as a join.
        const priorSessions = await getSessionsByUserId(currentUser.id);
        if (cancelled) return;
        priorAccess = (priorSessions || []).some(s => String(s.id) === String(sessionId));
        setHasGuiAccess(priorAccess);
      }

      if (isGui && isStudent && !priorAccess) {
        // Do not call joinSession here - that would create the very user_session row
        // that grants access, silently admitting an uninvited student on their first
        // (denied) visit.
        return;
      }

      // Compared as numbers: the route id is a string, while session_id can be a number (a
      // guest's, set at registration, which already counted them in stu_num) - a raw !==
      // re-ran the join below and counted the guest a second time.
      if (Number(currentUser.session_id) !== Number(sessionId)) {
        // Update the user's session information
        const updatedUser = {...currentUser};
        updatedUser.group_id = null;
        updatedUser.code_id = null;

        // Add session to user's session list if not already there. session_list is
        // stored as ARRAY(INTEGER) in Postgres, but sessionId here comes from
        // useParams() and is always a string - comparing/pushing it raw made
        // .includes() always miss (["719"] vs [719]), so this "already joined"
        // check silently failed on every visit and re-incremented stu_num every
        // time the link was opened, not just on the actual first join.
        if (!updatedUser.session_list?.includes(Number(sessionId))) {
          updatedUser.session_list = updatedUser.session_list || [];
          updatedUser.session_list.push(Number(sessionId));
          await IncrementStuNumInSession(sessionId);
        }

        // Update the session_id
        updatedUser.session_id = sessionId;

        // Save changes and update context
        await updateUser(updatedUser);
        if (cancelled) return;
        setCurrentUser(updatedUser);

        console.log("User session updated in Home.jsx");
      }

      // Join the session
      console.log("joining session from Home.jsx");
      joinSession(sessionId, currentUser.id, currentUser.uid);
    };

    run();
    return () => { cancelled = true };
    // Re-run whenever the route's session id changes, not just when currentUser changes -
    // React Router reuses this component across /session/:id -> /session/:otherId
    // navigations (same route pattern), so without routeSessionId in the dependency array
    // this effect never re-fires on that kind of navigation. That left SessionContext's
    // `session` holding the PREVIOUS session while the URL had already moved on, and the
    // GUI-redirect check below would then redirect using that stale session's own id.
    // routeRetry: the "Try again now" of a slow fetch.
  }, [currentUser, routeSessionId, routeRetry]);

  // A student's link to no session: check again now and then - an empty answer is also what a
  // busy server sends - and load the page by itself once the session is there.
  useEffect(() => {
    if (routeLoad !== 'missing') return undefined;
    let cancelled = false;
    const timer = setInterval(async () => {
      const found = await getSessionById(routeSessionId).catch(() => null);
      if (found && !cancelled) setRouteRetry((value) => value + 1);
    }, SLOW_RETRY_MS);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [routeLoad, routeSessionId]);

  // Determine if we should use VizmentalIDE. The rule lives in studentSurface.js so the
  // creation wizard's student-view preview and the student-layout Task tab route the same
  // way this page does.
  const useVizmentalIDE = usesVizmentalIDE(session)

  // GUI sessions are edited here (instructor) but attempted at /workspace/:id
  // (student) - see GUI_TRACE_TASKS.md. A student who lands on this route
  // for a GUI session (shared link, back button, etc.) gets sent there.
  // Role 3 = anonymous guest, role 4 = a real, self-registered student account - both are
  // students, not instructors/TAs (role <= 2), and both attempt GUI exercises at
  // /workspace/:id, never here.
  const isStudentRole = currentUser?.role === 3 || currentUser?.role === 4
  // session.id must match the route's own id, not just be truthy - guards against acting
  // on a stale session left over from a previous /session/:id the context hasn't replaced
  // yet (see the effect above).
  const sessionMatchesRoute = session && String(session.id) === String(routeSessionId)

  // Whether this route's row has been fetched (it may be missing: no such session). A row
  // from the previous route, before the effect above resets it, does not count.
  const routeLoaded =
    routeLoad === 'done' && (!routeSession || String(routeSession.id) === String(routeSessionId))

  if (isStudentRole) {
    // Their condition is elsewhere (or was, the last time they opened this link).
    if (entry.status === 'redirect') return <ExperimentEntryScreen entry={entry} />
    if (routeLoad === 'missing') {
      return (
        <ExperimentEntryScreen
          entry={{ ...missingSession(routeSessionId), retry: () => setRouteRetry((value) => value + 1) }}
        />
      )
    }
    // Until this route's row is here a student would see whichever session SessionContext
    // holds - often their previous one - or nothing: show that the page is loading.
    if (!routeLoaded) {
      return (
        <ExperimentEntryScreen
          entry={
            routeLoad === 'slow'
              ? { status: 'slow', retry: () => setRouteRetry((value) => value + 1) }
              : { status: 'checking' }
          }
        />
      )
    }
  }
  // An experiment's link, or a condition's link that may not be this student's: they wait
  // here (never on a blank page) until they are sent where they belong.
  if (routeSession && routeLoaded && entry.status !== 'open') {
    return <ExperimentEntryScreen entry={entry} />
  }
  if (sessionMatchesRoute && isGuiSession(session) && isStudentRole) {
    if (hasGuiAccess === false) {
      // Landed on the instructor's /session/:id URL for a GUI session with no prior
      // user_session row - i.e. never actually invited via a /workspace/:id link (see the
      // effect above). Don't redirect them into the exercise or render the instructor view.
      return (
        <div style={{ maxWidth: '480px', margin: '80px auto', textAlign: 'center', padding: '0 20px' }}>
          <h2 style={{ marginBottom: '8px' }}>You don't have access to this session</h2>
          <p style={{ color: '#6b7280' }}>
            Ask your instructor for the exercise link, or check your{' '}
            <a href="/session">session list</a> for exercises you've already joined.
          </p>
        </div>
      )
    }
    if (hasGuiAccess) {
      return <Navigate to={`/workspace/${session.id}`} replace />
    }
    // hasGuiAccess === null: still checking prior access. Say so - a null render here was a
    // white page for as long as that check took (or for good, if it never came back).
    return <div style={{ margin: '80px auto', textAlign: 'center', color: '#6b7280' }}>Loading session...</div>
  }

  // The other sessions /workspace runs (the Python family, and study sessions it runs by
  // id) are attempted there too. They have no prior-access gate, so students go straight
  // to /workspace; the effect above leaves the join to it.
  if (sessionMatchesRoute && isStudentRole && opensInWorkspace(session)) {
    return <Navigate to={`/workspace/${session.id}`} replace />
  }

  // The Session Control lobby's timer (Start / Pause / Reset) holds students in a waiting
  // room until the instructor starts it - on /workspace (WorkspacePage.jsx) and, for the
  // session types attempted here, on this page too. duration_minutes marks a session the
  // instructor chose to run on the timer.
  const holdsInWaitingRoom =
    isStudentRole && session?.status === 'pending' && session?.duration_minutes != null

  return (
    <div className="home">
      <SelectedTestProvider>
        <GroupsProvider sessionId={session?.id}>
          <GroupsFilterProvider>
            <ErrorContextProvider>
              <TestResultContextProvider>
                <AutoGroupingContextProvider>
                  <HeaderBanner />
                  {/* A condition of an experiment, opened by staff: say so, on every type's page. */}
                  {currentUser?.role <= 2 &&
                    routeSession &&
                    String(routeSession.id) === String(routeSessionId) &&
                    conditionTagOf(routeSession) && <ConditionBanner session={routeSession} />}
                  <PresenterListProvider>
                    <CurrentStepContextProvider>
                      <MessageProvider sessionId={session?.id}>
                        <div className="container">
                          {currentUser?.role <= 2 &&
                            isGuiSession(session) && (
                              <GuiExerciseEditor guiConfig={getGuiSessionConfig(session)} />
                            )}
                          {currentUser?.role <= 2 &&
                            !isGuiSession(session) &&
                            session?.type === 'Audio' && <AudioSessionPanel />}
                          {currentUser?.role <= 2 &&
                            !isGuiSession(session) &&
                            session?.type === 'Pop Quiz' && <PopQuizEditor />}
                          {currentUser?.role <= 2 &&
                            isReasoningGapSession(session) && <ReasoningGapInstructor />}
                          {currentUser?.role <= 2 &&
                            isExperimentSession(session) && <ExperimentPage />}
                          {currentUser?.role <= 2 &&
                            !isGuiSession(session) &&
                            !isReasoningGapSession(session) &&
                            !isExperimentSession(session) &&
                            session?.type !== 'Audio' &&
                            session?.type !== 'Pop Quiz' && (
                              <>
                                <SubmissionsProvider>
                                  <BasicTabs />
                                </SubmissionsProvider>
                              </>
                            )}

                          {holdsInWaitingRoom && <WaitingRoom session={session} />}
                          {isStudentRole && !holdsInWaitingRoom &&
                            session?.type === 'Audio' && <AudioStu />}
                          {isStudentRole && !holdsInWaitingRoom &&
                            session?.type === 'Pop Quiz' && (
                              // Taking the quiz (question UI, answer telemetry, results)
                              // is separate follow-up work, not built yet - this only
                              // makes sure a student landing here doesn't fall through
                              // to the regular coding UI (Test Cases/Unit Tests), which
                              // don't apply to a Pop Quiz and were never functional here.
                              <div style={{ maxWidth: '480px', margin: '80px auto', textAlign: 'center', padding: '0 20px' }}>
                                <h2 style={{ marginBottom: '8px' }}>{session.task || session.subject}</h2>
                                <p style={{ color: '#6b7280' }}>This quiz isn't open for students yet.</p>
                              </div>
                            )}
                          {/* A student who was last in another session (e.g. Group A) briefly
                              gets that session from SessionContext while this route's session
                              loads, so the quiz flickered to the other group's page and could
                              save work there. Only render the quiz for the session in the URL. */}
                          {isStudentRole && !holdsInWaitingRoom &&
                            isReasoningGapSession(session) && (
                              String(session.id) === String(routeSessionId)
                                ? <ReasoningGapStudent key={session.id} />
                                : <div style={{ margin: '80px auto', textAlign: 'center', color: '#6b7280' }}>Loading quiz...</div>
                            )}
                          {isStudentRole && !holdsInWaitingRoom &&
                            !isReasoningGapSession(session) &&
                            !isExperimentSession(session) &&
                            session?.type !== 'Audio' &&
                            session?.type !== 'Pop Quiz' && (
                              useVizmentalIDE ? (
                                <VizmentalIDE />
                              ) : (
                                <StudentPanel />
                              )
                            )}
                        </div>
                      </MessageProvider>
                    </CurrentStepContextProvider>
                  </PresenterListProvider>
                </AutoGroupingContextProvider>
              </TestResultContextProvider>
            </ErrorContextProvider>
          </GroupsFilterProvider>
        </GroupsProvider>
      </SelectedTestProvider>
    </div>
  )
})

export default Home